import { createRouter, createWebHistory } from 'vue-router';
import NotFound from '../views/NotFound.vue';

// Admin routes import
import AdminLogin from '../views/admin/auth/Login.vue';
import AdminForgotPassword from '../views/admin/auth/ForgotPassword.vue';
import AdminResetPassword from '../views/admin/auth/ResetPassword.vue';
import AdminLayout from '../views/admin/Layout.vue';
import AdminHome from '../views/admin/Home.vue';
import AdminIndex from '../views/admin/admins/AdminIndex.vue';
import AdminShow from '../views/admin/admins/AdminShow.vue';
import AdminUserIndex from '../views/admin/users/UserIndex.vue';
import AdminUserShow from '../views/admin/users/UserShow.vue';
import DataProviderIndex from '../views/admin/data-providers/DataProviderIndex.vue';
import DataProviderShow from '../views/admin/data-providers/DataProviderShow.vue';
import MasterDataIndex from '../views/admin/master-data/MasterDataIndex.vue';
import MasterDataShow from '../views/admin/master-data/MasterDataShow.vue';
import AdminChangePassword from '../views/admin/ChangePassword.vue';
import PostalCodeIndex from '../views/admin/postal-codes/PostalCodeIndex.vue';

// User routes import
import UserLogin from '../views/user/auth/Login.vue';
import UserSignup from '../views/user/auth/Signup.vue';
import UserForgotPassword from '../views/user/auth/ForgotPassword.vue';
import UserResetPassword from '../views/user/auth/ResetPassword.vue';
import UserActivate from '../views/user/auth/Activate.vue';
import UserLayout from '../views/user/Layout.vue';
import LayoutNoAuth from '../views/user/LayoutNoAuth.vue';
import UserHome from '../views/user/Home.vue';
import UserChangePassword from '../views/user/ChangePassword.vue';
import UserChangeEmailAddress from '../views/user/ChangeEmailAddress.vue';
import UserCompleteChangeEmailAddress from '../views/user/CompleteChangeEmailAddress.vue';
import UserTermsOfUse from '../views/user/legal/TermsOfUse.vue';
import UserPrivacyPolicy from '../views/user/legal/PrivacyPolicy.vue';
import UserExternalTools from '../views/user/legal/ExternalTools.vue';
import StatisticData from '../views/user/export/StatisticData.vue';
import LedgHomeImport from '../views/user/import/LedgHome.vue';
import UserForbidden from '../views/user/Forbidden.vue';
import MasterEventOrganizerIndex from '../views/user/master/event_organizers/EventOrganizerIndex.vue';
import MasterEventOrganizerForm from '../views/user/master/event_organizers/EventOrganizerForm.vue';
import MasterEventIndex from '../views/user/master/events/EventIndex.vue';
import MasterEventForm from '../views/user/master/events/EventForm.vue';
import EventIndex from '../views/user/events/EventIndex.vue';
import EventShow from '../views/user/events/EventShow.vue';
import AccommodationIndex from '../views/user/accommodation_bookings/AccommodationIndex.vue';

import { RouterView } from 'vue-router';
import { getCurrentUser } from '../api/user/AuthApi.js';
import { getCurrentAdmin } from '../api/admin/AuthApi.js';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // Admin routes
    { path: '/admin/login', name: 'admin.login', component: AdminLogin },
    { path: '/admin/forgot-password', name: 'admin.forgot-password', component: AdminForgotPassword },
    { path: '/admin/reset-password/:token', name: 'admin.reset-password', component: AdminResetPassword },

    {
      path: '/admin',
      component: AdminLayout,
      children: [
        { path: '', name: 'admin.home', component: AdminHome },
        { path: 'change-password', name: 'admin.change-password', component: AdminChangePassword },

        // 管理者
        {
          path: 'admins',
          component: RouterView,
          children: [
            { path: '', name: 'admin.admins.index', component: AdminIndex },
            { path: ':id', name: 'admin.admins.show', component: AdminShow },
          ]
        },

        // ユーザー
        {
          path: 'users',
          component: RouterView,
          children: [
            { path: '', name: 'admin.users.index', component: AdminUserIndex },
            { path: ':id', name: 'admin.users.show', component: AdminUserShow },
          ]
        },

        // Data Providers
        {
          path: 'data-providers',
          component: RouterView,
          children: [
            { path: '', name: 'admin.data-providers.index', component: DataProviderIndex },
            { path: ':id', name: 'admin.data-providers.show', component: DataProviderShow },
          ]
        },

        // Master Data
        {
          path: 'master-data',
          component: RouterView,
          children: [
            { path: '', name: 'admin.master-data.index', component: MasterDataIndex },
            { path: ':tableName', name: 'admin.master-data.show', component: MasterDataShow },
          ]
        },

        // Postal Codes
        {
          path: 'postal-codes',
          component: RouterView,
          children: [
            { path: '', name: 'admin.postal-codes.index', component: PostalCodeIndex },
          ]
        },
      ]
    },

    // User routes - Authenticated routes first
    {
      path: '',
      component: UserLayout,
      children: [
        { path: '', name: 'home', component: UserHome },
        { path: 'events', name: 'events', component: EventIndex },
        { path: 'events/:id', name: 'events.show', component: EventShow, meta: { requiresPermission: 'DETAIL' } },
        { path: 'accommodation_bookings', name: 'accommodation_bookings', component: AccommodationIndex, meta: { requiresPermission: 'DETAIL' } },
        { path: 'change-password', name: 'change-password', component: UserChangePassword },
        { path: 'change-email-address', name: 'change-email-address', component: UserChangeEmailAddress },
        {
          path: 'export/statistic_data',
          name: 'statistic_data',
          component: StatisticData,
          meta: { requiresPermission: 'VIEW' }
        },
        {
          path: 'import/ledghome',
          name: 'import_ledghome',
          component: LedgHomeImport,
          meta: { requiresPermission: 'EDIT' }
        },
        {
          path: 'master/event_organizers',
          name: 'master.event_organizers.index',
          component: MasterEventOrganizerIndex,
          meta: { requiresPermission: 'EDIT' }
        },
        {
          path: 'master/event_organizers/new',
          name: 'master.event_organizers.new',
          component: MasterEventOrganizerForm,
          meta: { requiresPermission: 'EDIT' }
        },
        {
          path: 'master/event_organizers/:id',
          name: 'master.event_organizers.edit',
          component: MasterEventOrganizerForm,
          meta: { requiresPermission: 'EDIT' }
        },
        {
          path: 'master/events',
          name: 'master.events.index',
          component: MasterEventIndex,
          meta: { requiresPermission: 'EDIT' }
        },
        {
          path: 'master/events/new',
          name: 'master.events.new',
          component: MasterEventForm,
          meta: { requiresPermission: 'EDIT' }
        },
        {
          path: 'master/events/:id',
          name: 'master.events.edit',
          component: MasterEventForm,
          meta: { requiresPermission: 'EDIT' }
        },
        { path: 'forbidden', name: 'forbidden', component: UserForbidden },
      ]
    },

    // Public routes (no authentication required)
    {
      path: '',
      component: LayoutNoAuth,
      children: [
        { path: '/login', name: 'login', component: UserLogin },
        { path: '/sign-up', name: 'signup', component: UserSignup },
        { path: '/forgot-password', name: 'forgot-password', component: UserForgotPassword },
        { path: '/reset-password/:token', name: 'reset-password', component: UserResetPassword },
        { path: '/complete-change-email-address/:token', name: 'complete-change-email-address', component: UserCompleteChangeEmailAddress },
        { path: '/activate', name: 'activate', component: UserActivate },
        { path: 'terms-of-use', name: 'terms-of-use', component: UserTermsOfUse },
        { path: 'privacy-policy', name: 'privacy-policy', component: UserPrivacyPolicy },
        { path: 'external-tools', name: 'external-tools', component: UserExternalTools },
      ]
    },

    // 404 Not Found
    { path: '/:catchAll(.*)', name: 'notfound', component: NotFound }
  ],
});

// Navigation guard for authentication and permission check
router.beforeEach(async (to, from, next) => {
  // Routes that don't require authentication
  const publicRoutes = [
    'login',
    'signup',
    'forgot-password',
    'reset-password',
    'complete-change-email-address',
    'activate',
    'terms-of-use',
    'privacy-policy',
    'external-tools',
    'admin.login',
    'admin.forgot-password',
    'admin.reset-password',
  ];

  // Check if route requires authentication
  const requiresAuth = !publicRoutes.includes(to.name);

  if (requiresAuth) {
    try {
      // Determine if this is an admin route
      const isAdminRoute = to.path.startsWith('/admin');

      // Try to get current user or admin based on route
      const currentEntity = isAdminRoute
        ? await getCurrentAdmin()
        : await getCurrentUser();

      // Check if route requires permission (only for user routes)
      if (!isAdminRoute && to.meta.requiresPermission) {
        console.log('Checking permission for route:', to.name);
        console.log('Required permission:', to.meta.requiresPermission);
        console.log('User permissions:', currentEntity.permissions);

        // Check if user has required permission
        const hasPermission = currentEntity.permissions?.some(
          permission => permission.permissionType === to.meta.requiresPermission
        );

        console.log('Has permission:', hasPermission);

        if (!hasPermission) {
          console.log('Permission denied, redirecting to forbidden page');
          // Redirect to forbidden page
          next({ name: 'forbidden' });
          return;
        }
      }
    } catch (error) {
      // User is not authenticated
      console.error('Authentication check error:', error);

      // Redirect to appropriate login page
      if (to.path.startsWith('/admin')) {
        next({ name: 'admin.login' });
      } else {
        next({ name: 'login' });
      }
      return;
    }
  }

  next();
});

export default router;
